import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

const TOC_BORDER = "#d1d5db"

/* EGDE-475 (round 2): the fixed rail lives in the gutter beside the centred
   content column. From 1600px up the free gutter beside the 1200px column —
   (100vw − 1200px) / 2 — fits the rail on its own. Below that the gutter is
   too narrow, but hiding the rail behind the mobile bottom bar on a desktop
   was rejected (Raz, 2026-07-13): the rail must stay and the PAGE CONTENT
   shrinks to the grid instead — text columns narrow and images scale with
   them. RailPageContent (applied by PageBlocks when the page has anchors)
   reserves that room, so between lg and 1600 the rail gets a fixed
   RAIL_COLUMN gutter to sit in. */
const RAIL_MIN_VIEWPORT = breakpoints.lg
/* Total inline-start room reserved for the rail at lg–1599: 24px viewport
   inset + up-to-160px label column + 16px breathing space to the content. */
const RAIL_COLUMN = 200
/* Above 1600 the natural gutter takes over and no content shrink is needed. */
const NATURAL_GUTTER_VIEWPORT = 1600

/* EGDE-475: wraps the page's blocks on pages that render the anchor rail.
   Between lg and 1600 it adds symmetric inline padding, narrowing every
   block's content column (text wraps earlier, `width: 100%` images scale
   down in proportion) so the rail never paints over the text. Combined with
   the Container's own 80–120px padding the reserved gutter is ≥ RAIL_COLUMN.
   Symmetric so the column stays centred like every other tier. */
export const RailPageContent = styled.div`
  width: 100%;

  @media (min-width: ${RAIL_MIN_VIEWPORT}px) and (max-width: ${NATURAL_GUTTER_VIEWPORT - 1}px) {
    padding-inline: ${RAIL_COLUMN - 80}px;
  }
`

/* ------------------------------------------------------------------ */
/* Floating anchor rail — EGDE-76                                       */
/* A page-level navigation listing every Rich Content Block anchor.     */
/* Desktop: a fixed rail pinned to the inline-START gutter (right in     */
/* RTL) and vertically centred. QA (2026-06-21) rejected the inline-end  */
/* (left in RTL) placement — "it should appear on the right".            */
/* Mobile (EGDE-341): the old fixed top strip sat below the header over  */
/* the WHOLE page and QA flagged it as "in the way". It is replaced by   */
/* the magazine article's pattern (EGDE-392/393): a collapsible bar      */
/* pinned to the BOTTOM of the viewport, shown only while the rich       */
/* content region is on screen. The Rail itself is desktop-only now.     */
/* ------------------------------------------------------------------ */
export const Rail = styled.nav<{ $visible?: boolean }>`
  display: none;

  /* EGDE-475 (round 3): the fixed rail painted over the footer once the page
     scrolled to the bottom — fade it out as soon as the footer's top reaches
     the rail's vertical band (measured in the scroll handler). */
  opacity: ${(p) => (p.$visible === false ? 0 : 1)};
  pointer-events: ${(p) => (p.$visible === false ? "none" : "auto")};
  transition: opacity 0.25s ease;

  @media (min-width: ${RAIL_MIN_VIEWPORT}px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${sizes.spacing[8]}px;
    position: fixed;
    inset-inline-start: ${sizes.spacing[24]}px;
    inset-inline-end: auto;
    top: 50%;
    transform: translateY(-50%);
    max-height: 70vh;
    /* EGDE-475: at lg–1599 the gutter is the fixed RAIL_COLUMN reserved by
       RailPageContent — the rail fills it minus its 24px inset and 16px
       breathing room. Labels wrap within it (EGDE-472). */
    max-width: ${RAIL_COLUMN - 40}px;
    overflow-x: visible;
    overflow-y: auto;
    z-index: 20;
    padding: 0;
    padding-inline-end: ${sizes.spacing[16]}px;
    /* Border faces the content side (inline-end, = left in RTL). */
    border-inline-end: 1px solid ${colors.primitives.neutral300};
  }

  @media (min-width: ${NATURAL_GUTTER_VIEWPORT}px) {
    /* The natural gutter beside the 1200px column is wide enough on its own —
       never wider than it, so long labels can't paint over the content. */
    max-width: min(260px, calc((100vw - 1200px) / 2 - 40px));
  }
`

export const AnchorItem = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  flex-shrink: 0;
  width: 100%;
  padding: ${sizes.spacing[12]}px ${sizes.spacing[16]}px;
  padding-inline-end: 0;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

export const AnchorText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  color: ${(p) =>
    p.$active ? colors.primitives.primary500 : colors.primitives.neutral600};
  font-weight: ${(p) => (p.$active ? 700 : 500)} !important;
  flex: 1;
  min-width: 0;
  /* EGDE-472: long section titles wrap onto extra lines — QA rejected the
     ellipsis truncation ("anchor shouldn't appear cut"). The rail's max-width
     still stops the label growing over the page content. */
  white-space: normal;
  overflow-wrap: break-word;
`

export const AnchorChevron = styled.div`
  width: 12px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

/* ------------------------------------------------------------------ */
/* Mobile bottom bar (EGDE-341) — mirrors the magazine article's        */
/* MobileTableOfContents (EGDE-392/393): fixed to the viewport bottom,   */
/* collapsed bar shows the active section, tapping opens the list        */
/* upward, and the whole bar slides away when the rich content region    */
/* leaves the screen.                                                    */
/* ------------------------------------------------------------------ */
export const MobileRoot = styled.div<{ $visible?: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: ${colors.primitives.neutral0};
  box-shadow: 0px -4px 20px rgba(38, 48, 61, 0.15);
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
  transform: translateY(${(p) => (p.$visible ? "0" : "100%")});
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};

  /* EGDE-475 round 2: the rail is back on every desktop width (the content
     shrinks to make room for it), so the bar is mobile/tablet-only again. */
  @media (min-width: ${RAIL_MIN_VIEWPORT}px) {
    display: none;
  }
`

/* Collapsed bar / toggle — shows the active section, opens the panel. */
export const MobileBar = styled.button`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  height: 54px;
  padding-inline: ${sizes.spacing[16]}px;
  background: ${colors.primitives.neutral0};
  border: none;
  border-block: 1px solid ${TOC_BORDER};
  cursor: pointer;
  text-align: start;
`

export const MobileBarText = styled(Typography)`
  flex: 1;
  min-width: 0;
  text-align: start;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700 !important;
  color: ${colors.primitives.neutral600};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

/* Bar sits at the bottom and the panel opens upward, so collapsed points up
   ("open"), expanded points down ("close"). ArrowDown points down, so rotate
   180 when closed. */
export const MobileCaret = styled.span<{ $open?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primitives.neutral600};
  transition: transform 0.2s ease;
  transform: rotate(${(p) => (p.$open ? "0deg" : "180deg")});
`

/* The dropdown list opens upward from the bottom bar. Absolute (not in flow) so
   opening it never reflows the page. */
export const MobilePanel = styled.div`
  position: absolute;
  bottom: 100%;
  inset-inline: 0;
  display: flex;
  flex-direction: column;
  background: ${colors.primitives.neutral0};
  box-shadow: 0px -8px 20px rgba(38, 48, 61, 0.15);
`

export const MobileRow = styled.a<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  height: 54px;
  padding-inline: ${sizes.spacing[16]}px;
  border-bottom: 1px solid ${TOC_BORDER};
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const MobileRowText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})<{ $active?: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: start;
  font-size: 18px;
  line-height: 22px;
  font-weight: ${(p) => (p.$active ? 700 : 500)} !important;
  color: ${colors.primitives.neutral600};
`
